import { dbService, firebaseInstance } from '../firebaseInstance';

const COLLECTIONS_COL = 'collection';
const CATEGORIES_COL = 'categories';

export const getCategories = async () => {
  const snapshot = await dbService.collection(CATEGORIES_COL).orderBy('order', 'asc').get().catch(async () => {
    // order 필드가 없을 수 있으므로 fallback
    return dbService.collection(CATEGORIES_COL).get();
  });
  return snapshot.docs.map((doc) => ({ key: doc.id, ...doc.data() }));
};

export const subscribeCategories = (onChange, onError) => {
  const unsub = dbService
    .collection(CATEGORIES_COL)
    .onSnapshot(
      (snap) => {
        const list = snap.docs
          .map((doc) => ({ key: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const ao = typeof a.order === 'number' ? a.order : Number.POSITIVE_INFINITY;
            const bo = typeof b.order === 'number' ? b.order : Number.POSITIVE_INFINITY;
            if (ao !== bo) return ao - bo;
            // tie-breaker by key for stable ordering
            return String(a.key).localeCompare(String(b.key));
          });
        onChange && onChange(list);
      },
      (err) => {
        onError && onError(err);
      }
    );
  return unsub;
};

export const createCategory = async (key, labels) => {
  const keyRegex = /^[A-Za-z0-9_-]{2,32}$/;
  if (!keyRegex.test(key)) {
    throw new Error('Invalid key. Use 2-32 chars: letters (A-Z,a-z), numbers, _ or -');
  }
  if (!labels || !labels.en || !labels.ko) {
    throw new Error('Both English (en) and Korean (ko) labels are required.');
  }
  const docRef = dbService.collection(CATEGORIES_COL).doc(key);
  const exists = await docRef.get();
  if (exists.exists) {
    throw new Error('Category key already exists.');
  }
  const now = new Date();
  await docRef.set({
    labels: {
      en: String(labels.en).trim(),
      ko: String(labels.ko).trim(),
    },
    isActive: true,
    createdAt: now,
    updatedAt: now,
  });
};

export const updateCategory = async (key, updates) => {
  const allowed = ['labels', 'isActive', 'order'];
  const payload = {};
  Object.keys(updates || {}).forEach((k) => {
    if (allowed.includes(k)) payload[k] = updates[k];
  });
  payload.updatedAt = new Date();
  await dbService.collection(CATEGORIES_COL).doc(key).set(payload, { merge: true });
};

export const deleteCategory = async (key) => {
  // 안전장치: 사용 중인 컬렉션이 있으면 삭제 차단
  const inUseSnap = await dbService
    .collection(COLLECTIONS_COL)
    .where('categoryKey', '==', key)
    .limit(1)
    .get();
  if (!inUseSnap.empty) {
    throw new Error('Cannot delete: category is in use by at least one collection.');
  }
  await dbService.collection(CATEGORIES_COL).doc(key).delete();
};

export const assignCategoryToCollection = async (collectionId, keysOrNull) => {
  const colRef = dbService.collection(COLLECTIONS_COL).doc(collectionId);
  const now = new Date();
  const prevSnap = await colRef.get();
  const prevData = prevSnap.exists ? prevSnap.data() : {};
  const prevKeys = Array.isArray(prevData.categoryKeys) ? prevData.categoryKeys : [];

  if (!keysOrNull || (Array.isArray(keysOrNull) && keysOrNull.length === 0)) {
    await colRef.set({ categoryKeys: [], categoryLabels: [], updatedAt: now }, { merge: true });
    // Remove collection from all previous categories' collectionIdList
    await Promise.all(prevKeys.map((k) => dbService.collection(CATEGORIES_COL).doc(k).update({
      collectionIdList: firebaseInstance.firestore.FieldValue.arrayRemove(collectionId),
      updatedAt: now,
    }).catch(() => {})));
    return { categoryKeys: [], categoryLabels: [] };
  }
  const keys = Array.isArray(keysOrNull) ? keysOrNull : [keysOrNull];
  // fetch categories and validate
  const reads = await Promise.all(keys.map((k) => dbService.collection(CATEGORIES_COL).doc(k).get()));
  const labelList = [];
  reads.forEach((snap, idx) => {
    if (!snap.exists) {
      throw new Error(`Category key does not exist: ${keys[idx]}`);
    }
    const data = snap.data();
    if (data.isActive === false) {
      throw new Error(`Cannot assign an inactive category: ${keys[idx]}`);
    }
    // Note: we don't know locale here; use ko → en → key for snapshot
    const label = (data.labels && (data.labels.ko || data.labels.en)) || keys[idx];
    labelList.push(label);
  });
  await colRef.set({ categoryKeys: keys, categoryLabels: labelList, updatedAt: now }, { merge: true });

  // Update categories membership lists (add new, remove old)
  const toAdd = keys.filter((k) => !prevKeys.includes(k));
  const toRemove = prevKeys.filter((k) => !keys.includes(k));
  await Promise.all([
    ...toAdd.map((k) => dbService.collection(CATEGORIES_COL).doc(k).set({
      collectionIdList: firebaseInstance.firestore.FieldValue.arrayUnion(collectionId),
      updatedAt: now,
    }, { merge: true })),
    ...toRemove.map((k) => dbService.collection(CATEGORIES_COL).doc(k).update({
      collectionIdList: firebaseInstance.firestore.FieldValue.arrayRemove(collectionId),
      updatedAt: now,
    }).catch(() => {})),
  ]);
  return { categoryKeys: keys, categoryLabels: labelList };
};


